// Tarjeta de persona/habitación. Orden espacial estable (no se reordena por estado).
// La densidad degrada la tarjeta tranquila, NUNCA la de excepción.
import { Activity, Radio, HeartPulse, BedDouble, WifiOff, CircleHelp, ShieldAlert } from "lucide-react";
import { acentoPri } from "@/lib/centroV3/data";

const toneCls = { warn: "text-[#B45309] bg-[#D97706]/10", bad: "text-[#9B2C2C] bg-[#9B2C2C]/10", ok: "text-emerald-700 bg-emerald-50", muted: "text-slate-500 bg-slate-100" };

function Obs({ p }) {
  if (p.compartida && !p.presencia) return <p className="text-xs text-slate-500">Telemetría individual no disponible</p>;
  if (p.observabilidad === "sin_sensor") return <p className="text-xs text-slate-500">Telemetría individual no disponible</p>;
  if (p.observabilidad === "sin_lectura") return <p className="flex items-center gap-1 text-xs text-slate-500"><WifiOff className="h-3 w-3" /> Sin datos de presencia</p>;
  if (!p.presencia) return <p className="flex items-center gap-1 text-xs text-slate-500"><CircleHelp className="h-3 w-3" /> Sin datos de presencia</p>;
  const mov = p.presencia.texto === "Movimiento detectado";
  // RADAR-002: si falló la consulta de lecturas, la última lectura se identifica como histórica.
  if (p.lecturaHistorica) return <p className="flex items-center gap-1.5 text-xs text-slate-500"><CircleHelp className="h-3 w-3 shrink-0" /> {p.presencia.texto} · <span className="tabular-nums">{p.presencia.hace}</span> · última lectura sin confirmar</p>;
  return <p className="flex items-center gap-1.5 text-xs text-slate-600"><Radio className={`h-3.5 w-3.5 shrink-0 ${mov ? "text-[#0891A6]" : "text-slate-400"}`} /> {p.presencia.texto} · <span className="tabular-nums text-slate-400">{p.presencia.hace}</span></p>;
}

export default function PersonaCard({ p, dense, onSelect, onAbrirFicha, selected }) {
  const excepcion = !!p.alarma;
  const ac = excepcion ? acentoPri(p.alarma.prioridad) : null;
  const activas = excepcion ? (p.alarma.alertasActivas || 1) : 0;
  const hideSecondary = dense && !excepcion; // nunca degradar la tarjeta de excepción
  // RADAR-005: el recorrido operativo prioriza la ALERTA. Sin alerta activa, la tarjeta abre la
  // ficha del residente; con alerta activa, abre el caso que requiere atención. "Ficha" siempre
  // está disponible como acción secundaria explícita.
  const abrirPrincipal = () => (excepcion && onSelect ? onSelect(p) : (onAbrirFicha || onSelect || (() => {}))(p));
  return (
    <div data-testid={`centro-persona-${p.id}`} role="button" tabIndex={0}
      aria-label={excepcion ? `${p.nombre} · ${activas > 1 ? `${activas} alertas activas` : "alerta activa"} · abrir el caso` : `${p.nombre} · sin alarma activa · abrir la ficha`}
      onClick={abrirPrincipal} onKeyDown={(e) => { if (e.key === "Enter") abrirPrincipal(); }}
      className={`cursor-pointer rounded-xl border bg-white p-3 transition-shadow hover:shadow-md focus:outline-none ${excepcion ? "shadow-md" : "border-slate-200"} ${selected ? "ring-2 ring-[#01CBE3] ring-offset-1" : ""}`}
      style={excepcion ? { borderColor: ac.hex, borderLeftWidth: 5 } : undefined}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-slate-800">{p.nombre}</p>
          {p.room && <p className="text-[11px] text-slate-400">{p.room}</p>}
        </div>
        {p.coberturaMarca && (
          <span title={p.coberturaMarca.label} className={`inline-flex shrink-0 items-center rounded px-1 py-0.5 ${toneCls[p.coberturaMarca.tone] || toneCls.muted}`}>
            <ShieldAlert className="h-3 w-3" />
          </span>
        )}
      </div>

      {excepcion ? (
        <div className="mt-2 rounded-lg px-2 py-1.5" style={{ backgroundColor: `${ac.hex}14` }}>
          <div className="flex items-center gap-1.5">
            <Activity className="h-4 w-4 shrink-0" style={{ color: ac.hex }} />
            <p className="text-[12px] font-extrabold uppercase leading-tight" style={{ color: ac.hex }}>{p.alarma.verbo}</p>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-[10px] font-bold" style={{ color: ac.hex }}>{p.alarma.prioridad.toUpperCase()} · {p.alarma.estado === "sin_tomar" ? "Pendiente" : "En atención"}</span>
            <span className="font-mono text-[11px] font-bold tabular-nums" style={{ color: ac.hex }}>{p.alarma.hace}</span>
          </div>
          {activas > 1 && (
            <p data-testid={`centro-persona-activas-${p.id}`} className="mt-1 text-[10px] font-bold" style={{ color: ac.hex }}>
              {activas} alertas activas · se abre la de mayor prioridad
            </p>
          )}
          {p.alarma.estado !== "sin_tomar" && <p className="text-[10px] text-slate-500">{p.alarma.responsable || "Central Galarya"}{p.alarma.operador ? ` · ${p.alarma.operador}` : ""}</p>}
        </div>
      ) : (
        <p className="mt-2 text-xs text-slate-500">Sin alarma activa</p>
      )}

      {excepcion && (
        <button data-testid={`centro-persona-ficha-${p.id}`} type="button"
          onClick={(e) => { e.stopPropagation(); (onAbrirFicha || onSelect || (() => {}))(p); }}
          className="mt-2 inline-flex min-h-8 items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-[#01CBE3] hover:text-[#0891A6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3]">
          Ficha
        </button>
      )}

      <div className="mt-2 flex flex-col gap-1.5">
        <Obs p={p} />
        {p.coberturaMarca && <p className={`flex w-fit items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${toneCls[p.coberturaMarca.tone] || toneCls.muted}`}><ShieldAlert className="h-3 w-3 shrink-0" /> {p.coberturaMarca.label}</p>}
        {!hideSecondary && p.vitals && (p.vitals.hr || p.vitals.resp) && (
          <p className="flex items-center gap-1.5 text-[11px] text-slate-500"><HeartPulse className="h-3 w-3 shrink-0 text-slate-400" />
            {p.vitals.hr ? `FC ${p.vitals.hr}` : ""}{p.vitals.hr && p.vitals.resp ? " · " : ""}{p.vitals.resp ? `Resp. ${p.vitals.resp}` : ""} · <span className="tabular-nums text-slate-400">{p.vitals.hace}</span>{p.lecturaHistorica ? " · última lectura sin confirmar" : ""}</p>
        )}
        {!hideSecondary && p.cama && p.cama !== "no_disponible" && (
          <p className="flex items-center gap-1.5 text-[11px] text-slate-500"><BedDouble className="h-3 w-3 shrink-0 text-slate-400" /> Último evento de cama: {p.cama.texto} · <span className="tabular-nums text-slate-400">{p.cama.hace}</span></p>
        )}
      </div>
    </div>
  );
}
